/**
 * 발송 경로(v1/v2) 관리 화면의 순수 함수 (SPEC-MSGD-004 REQ-401·404).
 *
 * 화면은 v1(기존 inapi)·v2(발송 서버) 표기만 보여 주고, 서버로는 저장값(inapi·dispatcher)만 보낸다.
 * 회사별 상태는 세 가지다: default(기본값 따름 = 회사 행 없음) / v1 / v2.
 * 자체 점검: node src/views/KakaoService/messageRouteState.selfcheck.mjs
 */

export const CHANNELS = [
  { key: "alimtalk", label: "알림톡" },
  { key: "sms", label: "문자" },
  { key: "brandmessage", label: "브랜드메시지" },
];

const ROUTE_LABEL = { inapi: "v1(기존 inapi)", dispatcher: "v2(발송 서버)" };
const ROUTE_SHORT = { inapi: "v1", dispatcher: "v2" };
const FOLLOW_DEFAULT = "기본값 따름";

// 화면 상태 → 저장값. 'default' 는 저장값이 아니라 회사 행 지우기다.
export const ROUTE_OF_STATE = { v1: "inapi", v2: "dispatcher" };
const STATE_OF_ROUTE = { inapi: "v1", dispatcher: "v2" };

export const DEFAULT_ROUTE_OPTIONS = [
  { value: "inapi", label: ROUTE_LABEL.inapi },
  { value: "dispatcher", label: ROUTE_LABEL.dispatcher },
];

export const COMPANY_STATE_OPTIONS = [
  { value: "default", label: FOLLOW_DEFAULT },
  { value: "v1", label: ROUTE_LABEL.inapi },
  { value: "v2", label: ROUTE_LABEL.dispatcher },
];

// 광고형 브랜드메시지는 재판매사 코드(svatbo_no)가 있어야 v2 로 나간다. 받아 시험하기 전까지 v2 를 막는다(crm 도 400).
export const V2_LOCKED = {
  brandmessage: "재판매사 코드(svatbo_no)를 받아 시험하기 전까지 브랜드메시지는 v2 로 바꿀 수 없습니다.",
};

/** 채널별 선택지. 막힌 채널은 v2 를 흐리게(이미 v2 인 값은 그대로 보이고 v1 으로는 바꿀 수 있다). */
export const optionsFor = (channel, options) =>
  V2_LOCKED[channel]
    ? options.map((o) => (o.value === "dispatcher" || o.value === "v2" ? { ...o, disabled: true } : o))
    : options;

const ACTION_LABEL = {
  set_default: "전체 기본 바꾸기",
  set_company: "회사 바꾸기",
  clear_company: "회사 지우기",
  clear_all_companies: "모두 지우기",
};

export const channelLabel = (channel) => (CHANNELS.find((c) => c.key === channel) || {}).label || channel;

export const routeLabel = (route) => ROUTE_LABEL[route] || route;

// 회사 행은 이름을 표시용으로만 들고 있다. 이름 없이 저장된 행(런북·API 로 바꾼 것)은 화면이 회사 id 로 찾은
// 이름(companies: { id: 이름 })을 쓰고, 그것도 없으면 id 를 보인다(B7).
export const companyLabel = (row, companies = {}) => row.companyName || companies[row.companyId] || row.companyId;

const kstDate = (t) => {
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? "" : new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10);
};

/**
 * 병원 검색 결과 한 줄에 붙이는 구분 정보. 같은 이름 병원이 여럿이라 이름만으로는 고를 수 없다.
 * 입력은 inapi /companies/list/v2 의 docs 한 건. 휴대폰 번호·대표자 이름은 싣지 않는다.
 */
export function companyDetail(co) {
  const addr = [co.addrOne, co.addrTwo].filter(Boolean).join(" ");
  const created = co.createAt ? kstDate(co.createAt) : "";
  return [
    co.accountInfo_username && `관리자 아이디 ${co.accountInfo_username}`,
    addr,
    created && `${created} 등록`,
    (co.isTestCompany === "Y" || co.isTestCompany === true) && "시험 회사",
    `ID ${co._id}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** 바꾼 사람: 계정 id → "이름(아이디)". 화면이 찾은 계정(accounts: { id: { name, username } })이 없으면 id 그대로. */
export function actorLabel(id, accounts = {}) {
  const account = id && accounts[id];
  if (!account) return id || "-";
  return account.name ? `${account.name}(${account.username})` : account.username || id;
}

/** 전체 기본값의 적용값. 행이 없으면 v1(inapi) 이다(B8). */
export function defaultRouteOf(defaults, channel) {
  const row = (defaults || []).find((d) => d.channel === channel);
  return (row && (row.effective || row.route)) || "inapi";
}

/** 회사 하나의 채널별 상태 { alimtalk, sms, brandmessage } — 'default' | 'v1' | 'v2'. */
export function companyStates(overrides, companyId) {
  const states = {};
  for (const { key } of CHANNELS) {
    const row = (overrides || []).find((o) => o.companyId === companyId && o.channel === key);
    states[key] = row ? STATE_OF_ROUTE[row.route] : "default";
  }
  return states;
}

export const overrideCount = (overrides, channel) => (overrides || []).filter((o) => o.channel === channel).length;

/** "고객사 전체 일괄 적용" 확인창 문구. 일괄 적용은 회사 개별 설정을 건드리지 않는다(B8). */
export function defaultConfirmText(channel, route, overrides) {
  const n = overrideCount(overrides, channel);
  const head = `${channelLabel(channel)} 전체 기본값을 ${routeLabel(route)}로 바꿉니다.`;
  return n
    ? `${head} 개별 설정이 있는 회사 ${n}곳은 그 설정을 계속 따릅니다.`
    : `${head} 개별 설정이 있는 회사가 없어 모든 회사가 이 값을 따릅니다.`;
}

/** 채널별 "개별 설정 모두 지우기" 확인창 문구. 전체 기본값 행은 남는다. */
export function clearAllConfirmText(channel, overrides, defaults) {
  const n = overrideCount(overrides, channel);
  const def = ROUTE_SHORT[defaultRouteOf(defaults, channel)];
  return `${channelLabel(channel)} 개별 설정 ${n}건을 모두 지웁니다. 이 회사들은 전체 기본값(${def})을 따릅니다.`;
}

/** 변경 이력 1건 → 표 1줄의 표기. lookups = 화면이 찾은 { accounts, companies }(없으면 id 를 보인다). */
export function historyRow(item, lookups = {}) {
  const row = {
    channel: channelLabel(item.channel),
    action: ACTION_LABEL[item.action] || item.action,
    by: actorLabel(item.by, lookups.accounts),
    note: item.note,
  };
  // 모두 지우기 요약은 companyId 가 null 이지만 전체 기본값을 바꾼 게 아니다(조율자 결정, AC-404a 와 다름).
  if (item.action === "clear_all_companies") {
    return { ...row, company: "회사 개별 설정 전체", before: "-", after: "-", deleted: `${item.deletedCount ?? 0}곳 지움` };
  }
  // 전체 기본 행이 없던 상태는 v1 으로 동작했고, 회사 행이 없는 상태는 기본값을 따른다.
  const none = item.companyId == null ? "설정 없음(v1)" : FOLLOW_DEFAULT;
  const label = (route) => (route ? routeLabel(route) : none);
  return {
    ...row,
    company: item.companyId == null ? "전체 기본값" : companyLabel(item, lookups.companies),
    before: label(item.before),
    after: label(item.after),
    deleted: "",
  };
}
